import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { 
  Heart, ArrowLeft, Users, Sparkles, Send, Loader2, 
  Clock, Check, MessageCircle, Gift, Flame, Calendar
} from "lucide-react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";

const WitnessCirclePage = () => {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [prompts, setPrompts] = useState(null);
  const [joining, setJoining] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [showJoinForm, setShowJoinForm] = useState(false);
  const [showCheckinForm, setShowCheckinForm] = useState(false);
  
  // Form states
  const [intention, setIntention] = useState("");
  const [reflection, setReflection] = useState("");
  const [gratitude, setGratitude] = useState("");
  const [challenge, setChallenge] = useState("");
  const [celebration, setCelebration] = useState("");

  useEffect(() => {
    if (token) {
      fetchStatus();
      fetchPrompts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchers are stable, re-run only on token change
  }, [token]);

  const fetchStatus = async () => {
    try {
      const response = await axios.get(`${API}/witness-circle/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStatus(response.data);
    } catch (error) {
      console.error("Error fetching status:", error);
      toast.error("Failed to load Witness Circle status");
    } finally {
      setLoading(false);
    }
  };

  const fetchPrompts = async () => {
    try {
      const response = await axios.get(`${API}/witness-circle/prompts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPrompts(response.data);
    } catch (error) {
      console.error("Error fetching prompts:", error);
    }
  };

  const handleJoin = async () => {
    if (!intention.trim()) {
      toast.error("Please share your intention for joining");
      return;
    }
    
    setJoining(true);
    try {
      const response = await axios.post(
        `${API}/witness-circle/join`,
        { intention },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(response.data.message);
      setShowJoinForm(false);
      fetchStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to join Witness Circle");
    } finally {
      setJoining(false);
    }
  };

  const handleCheckin = async () => {
    if (!reflection.trim() || !gratitude.trim()) {
      toast.error("Please complete the reflection and gratitude prompts");
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await axios.post(
        `${API}/witness-circle/checkin`,
        { reflection, gratitude, challenge, celebration },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(response.data.message);
      setShowCheckinForm(false);
      setReflection("");
      setGratitude("");
      setChallenge("");
      setCelebration("");
      fetchStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit check-in");
    } finally {
      setSubmitting(false);
    }
  };

  const handleLeave = async () => {
    if (!window.confirm("Are you sure you want to leave the Witness Circle? Your partner will be notified.")) {
      return;
    }
    
    setLeaving(true);
    try {
      const response = await axios.post(
        `${API}/witness-circle/leave`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(response.data.message);
      fetchStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to leave Witness Circle");
    } finally {
      setLeaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-24">
      {/* Header */}
      <header className="bg-gradient-to-r from-teal-500/10 via-emerald-500/10 to-amber-500/10 border-b border-border/50">
        <div className="max-w-4xl mx-auto px-6 py-8">
          <button
            onClick={() => navigate("/dashboard")}
            className="flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Dashboard
          </button>
          
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-teal-500 to-amber-500 flex items-center justify-center">
              <Users className="w-8 h-8 text-white" strokeWidth={1.5} />
            </div>
            <div>
              <h1 className="font-heading text-3xl font-semibold text-foreground">
                Witness Circle
              </h1>
              <p className="text-muted-foreground mt-1">
                Soul-to-soul accountability partnership
              </p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8" data-testid="witness-circle-page">
        
        {/* Not Joined State */}
        {status?.status === "not_joined" && !showJoinForm && (
          <div className="space-y-8">
            {/* Introduction */}
            <Card className="glass-card rounded-3xl border-0 overflow-hidden">
              <CardContent className="p-8">
                <div className="text-center mb-8">
                  <div className="w-20 h-20 rounded-full bg-gradient-to-br from-teal-500/20 to-amber-500/20 flex items-center justify-center mx-auto mb-6">
                    <Heart className="w-10 h-10 text-teal-500" strokeWidth={1.5} />
                  </div>
                  <h2 className="font-heading text-2xl font-semibold text-foreground mb-4">
                    Journey Together, Grow Together
                  </h2>
                  <p className="text-muted-foreground max-w-xl mx-auto leading-relaxed">
                    The Witness Circle connects you with an anonymous soul companion who is on a similar path of self-discovery. Together, you'll share weekly reflections, celebrate victories, and hold space for each other's growth.
                  </p>
                </div>

                {/* Features Grid */}
                <div className="grid md:grid-cols-3 gap-6 mb-8">
                  <div className="text-center p-4">
                    <div className="w-12 h-12 rounded-full bg-teal-500/10 flex items-center justify-center mx-auto mb-3">
                      <Sparkles className="w-6 h-6 text-teal-500" />
                    </div>
                    <h3 className="font-semibold text-foreground mb-2">Anonymous & Safe</h3>
                    <p className="text-sm text-muted-foreground">
                      Connect through soul names. No personal details shared.
                    </p>
                  </div>
                  
                  <div className="text-center p-4">
                    <div className="w-12 h-12 rounded-full bg-amber-500/10 flex items-center justify-center mx-auto mb-3">
                      <Calendar className="w-6 h-6 text-amber-500" />
                    </div>
                    <h3 className="font-semibold text-foreground mb-2">Weekly Check-ins</h3>
                    <p className="text-sm text-muted-foreground">
                      Share reflections, gratitudes, and celebrations every week.
                    </p>
                  </div>
                  
                  <div className="text-center p-4">
                    <div className="w-12 h-12 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto mb-3">
                      <MessageCircle className="w-6 h-6 text-emerald-500" />
                    </div>
                    <h3 className="font-semibold text-foreground mb-2">Gentle Accountability</h3>
                    <p className="text-sm text-muted-foreground">
                      Be witnessed in your growth without judgment.
                    </p>
                  </div>
                </div>

                <div className="text-center">
                  <Button 
                    onClick={() => setShowJoinForm(true)}
                    className="bg-gradient-to-r from-teal-500 to-amber-500 hover:from-teal-600 hover:to-amber-600 text-white px-8 py-6 text-lg rounded-2xl"
                    data-testid="join-witness-circle-btn"
                  >
                    <Heart className="w-5 h-5 mr-2" />
                    Find My Soul Companion
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Join Form */}
        {showJoinForm && (
          <Card className="glass-card rounded-3xl border-0">
            <CardContent className="p-8">
              <h2 className="font-heading text-2xl font-semibold text-foreground mb-6 text-center">
                Set Your Intention
              </h2>
              <p className="text-muted-foreground text-center mb-8">
                Before we match you with a soul companion, share your intention for joining.
              </p>
              
              <div className="max-w-xl mx-auto space-y-6">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Why do you want to join the Witness Circle?
                  </label>
                  <Textarea
                    placeholder="I want to be witnessed in my journey because..."
                    value={intention}
                    onChange={(e) => setIntention(e.target.value)}
                    className="min-h-[120px] rounded-xl"
                    data-testid="intention-input"
                  />
                </div>
                
                <div className="flex gap-4 justify-center">
                  <Button
                    variant="outline"
                    onClick={() => setShowJoinForm(false)}
                    className="rounded-xl"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleJoin}
                    disabled={joining || !intention.trim()}
                    className="bg-gradient-to-r from-teal-500 to-amber-500 text-white rounded-xl"
                    data-testid="submit-join-btn"
                  >
                    {joining ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <Heart className="w-4 h-4 mr-2" />
                    )}
                    Join the Circle
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Waiting State */}
        {status?.status === "waiting" && (
          <Card className="glass-card rounded-3xl border-0">
            <CardContent className="p-8 text-center">
              <div className="w-20 h-20 rounded-full bg-amber-500/20 flex items-center justify-center mx-auto mb-6">
                <Clock className="w-10 h-10 text-amber-500" strokeWidth={1.5} />
              </div>
              <h2 className="font-heading text-2xl font-semibold text-foreground mb-4">
                Finding Your Soul Companion
              </h2>
              <p className="text-muted-foreground max-w-md mx-auto mb-6">
                We're matching you with someone on a similar journey. This usually takes a few days as we ensure compatibility.
              </p>
              
              <div className="bg-muted/50 rounded-xl p-4 max-w-md mx-auto mb-6">
                <p className="text-sm text-muted-foreground">
                  <span className="font-medium">Your intention:</span><br />
                  "{status.intention}"
                </p>
              </div>
              
              <Button
                variant="ghost"
                onClick={handleLeave}
                disabled={leaving}
                className="text-muted-foreground hover:text-foreground"
              >
                {leaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Leave Queue
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Partnered State */}
        {status?.status === "partnered" && (
          <div className="space-y-8">
            {/* Partnership Card */}
            <Card className="glass-card rounded-3xl border-0 bg-gradient-to-br from-teal-500/5 to-amber-500/5">
              <CardContent className="p-8">
                <div className="flex flex-col md:flex-row items-center justify-between gap-6 mb-8">
                  <div className="text-center md:text-left">
                    <p className="text-sm text-muted-foreground mb-2">You are</p>
                    <h2 className="font-heading text-2xl font-semibold text-foreground">
                      {status.my_soul_name}
                    </h2>
                  </div>
                  
                  <div className="flex items-center gap-4">
                    <Heart className="w-8 h-8 text-pink-500" fill="currentColor" />
                  </div>
                  
                  <div className="text-center md:text-right">
                    <p className="text-sm text-muted-foreground mb-2">Connected with</p>
                    <h2 className="font-heading text-2xl font-semibold text-foreground">
                      {status.partner_soul_name}
                    </h2>
                  </div>
                </div>
                
                <div className="grid grid-cols-3 gap-4 text-center bg-white/50 dark:bg-white/5 rounded-2xl p-4">
                  <div>
                    <p className="text-2xl font-bold text-foreground">{status.weeks_together}</p>
                    <p className="text-sm text-muted-foreground">Weeks Together</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-foreground">{status.total_checkins || 0}</p>
                    <p className="text-sm text-muted-foreground">Your Check-ins</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-foreground">{status.partner_total_checkins || 0}</p>
                    <p className="text-sm text-muted-foreground">Partner Check-ins</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Weekly Check-in */}
            {!showCheckinForm ? (
              <Card className="glass-card rounded-3xl border-0">
                <CardContent className="p-8 text-center">
                  <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4">
                    <Send className="w-8 h-8 text-green-500" strokeWidth={1.5} />
                  </div>
                  <h3 className="font-heading text-xl font-semibold text-foreground mb-2">
                    Weekly Reflection
                  </h3>
                  <p className="text-muted-foreground mb-6">
                    Share your journey with {status.partner_soul_name}
                  </p>
                  <Button
                    onClick={() => setShowCheckinForm(true)}
                    className="bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-xl"
                    data-testid="start-checkin-btn"
                  >
                    <MessageCircle className="w-4 h-4 mr-2" />
                    Write Check-in
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <Card className="glass-card rounded-3xl border-0">
                <CardContent className="p-8">
                  <h3 className="font-heading text-xl font-semibold text-foreground mb-6 text-center">
                    This Week's Reflection
                  </h3>
                  
                  <div className="space-y-6 max-w-2xl mx-auto">
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        💭 {prompts?.prompts?.reflection || "What moment brought you closest to your authentic self?"}
                      </label>
                      <Textarea
                        placeholder="Share your reflection..."
                        value={reflection}
                        onChange={(e) => setReflection(e.target.value)}
                        className="min-h-[100px] rounded-xl"
                        data-testid="reflection-input"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        🙏 {prompts?.prompts?.gratitude || "What are you grateful for?"}
                      </label>
                      <Textarea
                        placeholder="Share your gratitudes..."
                        value={gratitude}
                        onChange={(e) => setGratitude(e.target.value)}
                        className="min-h-[80px] rounded-xl"
                        data-testid="gratitude-input"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        🌱 {prompts?.prompts?.challenge || "What challenge arose?"} (Optional)
                      </label>
                      <Textarea
                        placeholder="Share if you wish..."
                        value={challenge}
                        onChange={(e) => setChallenge(e.target.value)}
                        className="min-h-[80px] rounded-xl"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        🎉 {prompts?.prompts?.celebration || "What deserves celebration?"} (Optional)
                      </label>
                      <Textarea
                        placeholder="Share a win..."
                        value={celebration}
                        onChange={(e) => setCelebration(e.target.value)}
                        className="min-h-[80px] rounded-xl"
                      />
                    </div>
                    
                    <div className="flex gap-4 justify-center pt-4">
                      <Button
                        variant="outline"
                        onClick={() => setShowCheckinForm(false)}
                        className="rounded-xl"
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={handleCheckin}
                        disabled={submitting || !reflection.trim() || !gratitude.trim()}
                        className="bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-xl"
                        data-testid="submit-checkin-btn"
                      >
                        {submitting ? (
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        ) : (
                          <Send className="w-4 h-4 mr-2" />
                        )}
                        Share with {status.partner_soul_name}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Partner's Check-ins */}
            {status.partner_checkins?.length > 0 && (
              <Card className="glass-card rounded-3xl border-0">
                <CardContent className="p-8">
                  <h3 className="font-heading text-xl font-semibold text-foreground mb-6">
                    {status.partner_soul_name}'s Journey
                  </h3>
                  
                  <div className="space-y-6">
                    {status.partner_checkins.map((checkin, idx) => (
                      <div key={checkin.id || idx} className="bg-teal-500/5 rounded-2xl p-6">
                        <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
                          <Calendar className="w-4 h-4" />
                          Week {checkin.week_number} • {new Date(checkin.created_at).toLocaleDateString()}
                        </div>
                        
                        {checkin.reflection && (
                          <div className="mb-4">
                            <p className="text-sm font-medium text-muted-foreground mb-1">Reflection</p>
                            <p className="text-foreground">{checkin.reflection}</p>
                          </div>
                        )}
                        
                        {checkin.gratitude && (
                          <div className="mb-4">
                            <p className="text-sm font-medium text-muted-foreground mb-1">Gratitude</p>
                            <p className="text-foreground">{checkin.gratitude}</p>
                          </div>
                        )}
                        
                        {checkin.celebration && (
                          <div className="mb-4">
                            <p className="text-sm font-medium text-muted-foreground mb-1">Celebration 🎉</p>
                            <p className="text-foreground">{checkin.celebration}</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Leave Option */}
            <div className="text-center">
              <Button
                variant="ghost"
                onClick={handleLeave}
                disabled={leaving}
                className="text-muted-foreground hover:text-destructive"
              >
                {leaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Leave Witness Circle
              </Button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default WitnessCirclePage;
